import { lazy, type ComponentType } from 'react';
import { ModuleRegistry } from './core/modules';
import { APP_REGISTRY, type CyzorAppId } from './core/navigation/appRegistry';
import { ComingSoonAppRouter } from './apps/_shared/ComingSoonAppRouter';
import type { View } from './types';

const ControlAppRouter = lazy(() => import('./apps/control/AppRouter').then((module) => ({ default: module.ControlAppRouter })));
const CRMAppRouter = lazy(() => import('./apps/crm/AppRouter').then((module) => ({ default: module.CRMAppRouter })));
const ERPAppRouter = lazy(() => import('./apps/erp/AppRouter').then((module) => ({ default: module.ERPAppRouter })));
const ProjectsAppRouter = lazy(() => import('./apps/projects/AppRouter').then((module) => ({ default: module.ProjectsAppRouter })));
const PeopleAppRouter = lazy(() => import('./apps/people/AppRouter').then((module) => ({ default: module.PeopleAppRouter })));
const DocsAppRouter = lazy(() => import('./apps/docs/AppRouter').then((module) => ({ default: module.DocsAppRouter })));
const NeuralAppRouter = lazy(() => import('./apps/neural/AppRouter').then((module) => ({ default: module.NeuralAppRouter })));
const AutomateAppRouter = lazy(() => import('./apps/automate/AppRouter').then((module) => ({ default: module.AutomateAppRouter })));
const BIAppRouter = lazy(() => import('./apps/bi/AppRouter').then((module) => ({ default: module.BIAppRouter })));
const FormsAppRouter = lazy(() => import('./apps/forms/AppRouter').then((module) => ({ default: module.FormsAppRouter })));
const SignAppRouter = lazy(() => import('./apps/sign/AppRouter').then((module) => ({ default: module.SignAppRouter })));


const legacyViewsByApp: Record<CyzorAppId, readonly string[]> = {
  control: ['dashboard', 'pulse', 'empresas', 'agenda', 'configuracoes', 'settings', 'workspace-settings', 'workspace-members', 'workspace-roles', 'workspace-modules', 'perfil'],
  crm: ['clientes', 'cliente-workspace'],
  erp: ['produtos', 'produto-workspace', 'financeiro'],
  projects: ['projetos', 'projeto-workspace', 'ideias', 'ideia-workspace', 'roadmap'],
  people: ['equipe', 'membro-workspace'],
  docs: ['documentacao', 'keep'],
  bi: ['bi'],
  automate: ['flow-builder'],
  forms: ['forms'],
  sign: ['sign'],
  neural: ['ia', 'neural-settings'],
  finance: ['finance'],
  sales: ['sales'],
  customer: ['customer'],
  growth: ['growth'],
  operations: ['operations'],
  procurement: ['procurement'],
  supply: ['supply'],
  production: ['production'],
  quality: ['quality'],
  enterprise: ['enterprise'],
  technology: ['technology'],
  cybersecurity: ['cybersecurity'],
  data: ['data'],
  knowledge: ['knowledge'],
  product: ['product'],
  innovation: ['innovation'],
  ai: ['ai'],
  automation: ['automation'],
  facilities: ['facilities'],
  assets: ['assets'],
  communication: ['communication'],
  'health-safety': ['health-safety'],
  international: ['international'],
  franchise: ['franchise'],
  ecommerce: ['ecommerce'],
  ecosystem: ['ecosystem'],
  research: ['research'],
};

function createComingSoonRouter(appId: CyzorAppId): ComponentType<{ currentView: View | 'admin' }> {
  return function ComingSoonRouter({ currentView }: { currentView: View | 'admin' }) {
    return <ComingSoonAppRouter currentView={currentView as never} appId={appId} />;
  };
}

const routers: Record<CyzorAppId, ComponentType<{ currentView: View | 'admin' }>> = {
  control: ControlAppRouter,
  crm: CRMAppRouter,
  erp: ERPAppRouter,
  projects: ProjectsAppRouter,
  people: PeopleAppRouter,
  docs: DocsAppRouter,
  neural: NeuralAppRouter,
  automate: AutomateAppRouter,
  bi: BIAppRouter,
  forms: FormsAppRouter,
  sign: SignAppRouter,
  finance: createComingSoonRouter('finance'),
  sales: createComingSoonRouter('sales'),
  customer: createComingSoonRouter('customer'),
  growth: createComingSoonRouter('growth'),
  operations: createComingSoonRouter('operations'),
  procurement: createComingSoonRouter('procurement'),
  supply: createComingSoonRouter('supply'),
  production: createComingSoonRouter('production'),
  quality: createComingSoonRouter('quality'),
  enterprise: createComingSoonRouter('enterprise'),
  technology: createComingSoonRouter('technology'),
  cybersecurity: createComingSoonRouter('cybersecurity'),
  data: createComingSoonRouter('data'),
  knowledge: createComingSoonRouter('knowledge'),
  product: createComingSoonRouter('product'),
  innovation: createComingSoonRouter('innovation'),
  ai: createComingSoonRouter('ai'),
  automation: createComingSoonRouter('automation'),
  facilities: createComingSoonRouter('facilities'),
  assets: createComingSoonRouter('assets'),
  communication: createComingSoonRouter('communication'),
  'health-safety': createComingSoonRouter('health-safety'),
  international: createComingSoonRouter('international'),
  franchise: createComingSoonRouter('franchise'),
  ecommerce: createComingSoonRouter('ecommerce'),
  ecosystem: createComingSoonRouter('ecosystem'),
  research: createComingSoonRouter('research'),
};

export const moduleRegistry = new ModuleRegistry();

(Object.keys(APP_REGISTRY) as CyzorAppId[]).forEach((appId) => {
  const app = APP_REGISTRY[appId];
  const Router = routers[appId];
  moduleRegistry.register({
    id: app.id,
    slug: app.slug,
    name: app.name,
    description: app.description,
    version: app.version,
    icon: app.icon,
    basePath: app.basePath,
    defaultRoute: app.dashboardPath,
    route: app.route,
    category: app.category,
    status: app.status as any,
    navigation: app.navigation,
    permissions: app.permissions,
    capabilities: app.capabilities,
    dependencies: app.dependencies,
    requiresEntitlement: appId === 'control' ? undefined : appId,
    enabled: app.enabled,
    routes: [{
      id: `${app.id}-legacy-entry`,
      path: app.basePath,
      legacyViews: legacyViewsByApp[appId],
      render: (view) => <Router currentView={view as View | 'admin'} />,
    }],
  });
});
